import type { Job } from "@malfak/contracts";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiKey, useApiMutation, useApiQuery } from "@/lib/api/hooks";
import { isApiError } from "@/lib/api/errors";
import { useLiveEvent } from "@/lib/api/events";
import { useT } from "@/lib/i18n";
import { isActive, patchJobCaches, refreshJobsSoon, type JobsPage } from "./model";
import { announceJob } from "./toasts";

/** A job from any cached list, so a tray/list entry renders instantly before its own fetch. */
function fromLists(qc: QueryClient, jobId: string): Job | undefined {
  for (const [, data] of qc.getQueriesData<JobsPage>({ queryKey: ["jobs"] })) {
    const hit = data?.items?.find((j) => j.id === jobId);
    if (hit) return hit;
  }
  return undefined;
}

/** One job, kept live: SSE progress patches the cache, a debounced refetch brings ETA/controls; polls slowly as a fallback. */
export function useJob(jobId: string, opts: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  const q = useApiQuery(
    "job",
    { params: { jobId } },
    {
      staleTime: 5_000,
      enabled: opts.enabled ?? true,
      initialData: () => fromLists(qc, jobId),
      initialDataUpdatedAt: 0,
      refetchInterval: (query) => (query.state.data && isActive(query.state.data.status) ? 15_000 : false),
    },
  );
  useLiveEvent(
    (e) => {
      if (e.type !== "operation.updated" || e.operationId !== jobId) return;
      patchJobCaches(qc, e);
      refreshJobsSoon(qc, jobId);
    },
    [jobId, qc],
  );
  return q;
}

export function useJobCounts() {
  return useApiQuery("jobCounts", {}, { staleTime: 10_000, refetchInterval: 30_000 });
}

/** cancel / pause / resume / retry with cache updates; retry announces the new job (start toast + tray). */
export function useJobAction(kind: "cancelJob" | "pauseJob" | "resumeJob" | "retryJob") {
  const { t } = useT();
  const qc = useQueryClient();
  return useApiMutation(kind, {
    onSuccess: (job: Job, vars) => {
      const jobId = (vars.params as { jobId: string }).jobId;
      if (kind === "retryJob") {
        qc.setQueryData(apiKey("job", { params: { jobId: job.id } }), job);
        announceJob(job);
      } else {
        qc.setQueryData(apiKey("job", { params: { jobId } }), job);
        patchJobCaches(qc, { operationId: job.id, status: job.status, done: job.done, total: job.total, failed: job.failed });
      }
      refreshJobsSoon(qc, jobId, 100);
    },
    onError: (e) => toast.error(isApiError(e) && e.status === 409 ? t("jobs.conflict") : t("common.states.error")),
  });
}
